import { customElement, property } from "lit/decorators.js";
import { html, PropertyValues, TemplateResult } from "lit";
import { UserAnnotationFormData, userAnnotationState } from "@/state/UserAnnotations";
import { annotationState, compareAnnotationOrders } from "@/state/Annotations";
import { submissionState } from "@/state/Submissions";
import { MachineAnnotation, machineAnnotationState } from "@/state/MachineAnnotations";
import "@/components/annotations/machine_annotation";
import "@/components/annotations/user_annotation";
import "@/components/annotations/annotation_form";
import "@/components/annotations/thread";
import { AnnotationForm } from "@/components/annotations/annotation_form";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import { evaluationState } from "@/state/Evaluations";
import { DodonaElement } from "@/components/meta/dodona_element";

/**
 * This component represents a cell that groups all annotations for a specific line.
 * It also contains the form for creating new annotations.
 *
 * @element d-annotations-cell
 *
 * @prop {number} row - The line number
 * @prop {boolean} formShown - Whether the form should be shown
 *
 * @fires close-form - If the form should be closed
 */
@customElement("d-annotations-cell")
export class AnnotationsCell extends DodonaElement {
    @property({ type: Boolean, attribute: "show-form" })
    formShown = false;

    @property({ type: Number })
    row: number;

    annotationFormRef: Ref<AnnotationForm> = createRef();

    get machineAnnotations(): MachineAnnotation[] {
        return machineAnnotationState.byLine.get(this.row) || [];
    }

    get userAnnotationIds(): number[] {
        return userAnnotationState.rootIdsByLine.get(this.row) || [];
    }

    onSubmit(e: CustomEvent): void {
        void this.createAnnotation(e);
    }

    onCancel(): void {
        this.closeForm();
    }

    async createAnnotation(e: CustomEvent): Promise<void> {
        const annotationData: UserAnnotationFormData = {
            annotation_text: e.detail.text,
            line_nr: this.row,
            evaluation_id: evaluationState.id,
            saved_annotation_id: e.detail.savedAnnotationId || undefined,
        };

        if (this.row > 0 && userAnnotationState.selectedRange) {
            annotationData["line_nr"] = userAnnotationState.selectedRange.row;
            annotationData["rows"] = userAnnotationState.selectedRange.rows;
            annotationData["column"] = userAnnotationState.selectedRange.column;
            annotationData["columns"] = userAnnotationState.selectedRange.columns;
        }

        try {
            const mode = annotationState.isQuestionMode ? "question" : "annotation";
            await userAnnotationState.create(
                annotationData,
                submissionState.id,
                mode,
                e.detail.saveAnnotation,
                e.detail.savedAnnotationTitle,
            );
            this.closeForm();
        } catch {
            const form = this.annotationFormRef.value;
            form.hasErrors = true;
            form.disabled = false;
        }
    }

    closeForm(): void {
        const event = new CustomEvent("close-form", { bubbles: true, composed: true });
        this.dispatchEvent(event);

        // A11y: keep keyboard users anchored somewhere sensible after closing
        this.focus();
    }

    protected updated(changed: PropertyValues): void {
        // A11y: when the form becomes visible, focus the textarea inside it
        if (changed.has("formShown") && this.formShown) {
            void this.focusForm();
        }
    }

    private async focusForm(): Promise<void> {
        await this.updateComplete; // ensure <d-annotation-form> is in the DOM
        const form = this.annotationFormRef.value;
        await form.updateComplete; // ensure textarea exists inside the form
        form.focusTextArea();
    }

    protected render(): TemplateResult {
        const threads = this.userAnnotationIds;
        const machines = this.machineAnnotations
            .filter((a) => annotationState.isVisible(a))
            .slice()
            .sort(compareAnnotationOrders);

        return html`
            <div class="annotation-cell" tabindex="-1">
                ${
                    this.formShown
                        ? html`
                              <div class="annotation ${annotationState.isQuestionMode ? "question" : "user"}">
                                  <d-annotation-form
                                      @submit=${this.onSubmit}
                                      @cancel=${this.onCancel}
                                      ${ref(this.annotationFormRef)}
                                      submit-button-text="send"
                                  ></d-annotation-form>
                              </div>
                          `
                        : ""
                }
                ${threads.map((a) => html`<d-thread .rootId=${a}></d-thread>`)}
                ${machines.map((a) => html`<d-machine-annotation .data=${a}></d-machine-annotation>`)}
            </div>
        `;
    }
}
